import React, { useState, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Button,
  Banner,
  TextContainer,
  List,
  Modal,
  DataTable,
  Badge,
  Text,
  Link,
  Icon,
  Tooltip,
  InlineStack,
  BlockStack
} from '@shopify/polaris';
import { ImportIcon } from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAxiosClient from '../axiosClient';
import * as XLSX from 'xlsx';

export default function BulkImportStaff() {
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [hasHeaders, setHasHeaders] = useState(true);
  const [errors, setErrors] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('csv'); // 'csv' or 'excel'

  // Sample CSV template using translation keys
  const csvTemplate = `${t('template_headers.first_name')},${t('template_headers.last_name')},${t('template_headers.pin_code')},${t('template_headers.email')},${t('template_headers.phone')},${t('template_headers.permission')},${t('template_headers.currency')},${t('template_headers.payroll_type')},${t('template_headers.salary_amount')}
John,Doe,1234,john.doe@example.com,5550123,Staff,USD,Hourly,15.00`;

  // Sample Excel template data using translation keys
  const excelTemplateData = [
    [t('template_headers.first_name'), t('template_headers.last_name'), t('template_headers.pin_code'), t('template_headers.email'), t('template_headers.phone'), t('template_headers.permission'), t('template_headers.currency'), t('template_headers.payroll_type'), t('template_headers.salary_amount')],
    ['John', 'Doe', '1234', 'john.doe@example.com', '5550123', 'Staff', 'USD', 'Hourly', '15.00'],
  ];

  const DownloadIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  );

  const CSVIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
      <path d="M14 2v6h6"/>
      <path d="M8 13h8"/>
      <path d="M8 17h8"/>
      <path d="M8 9h8"/>
    </svg>
  );

  const ExcelIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
      <path d="M14 2v6h6"/>
      <path d="M8 13l3 3m0-3l-3 3"/>
      <path d="M8 17h8"/>
      <path d="M8 9h8"/>
    </svg>
  );

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check for both CSV and Excel file types
      const validTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/octet-stream' // Some systems may use this for CSV
      ];
      
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (!validTypes.includes(file.type) && !hasValidExtension) {
        setErrors([t('please_select_valid_csv_excel_file')]);
        return;
      }
      setSelectedFile(file);
      setErrors([]);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setErrors([t('please_select_csv_excel_file_to_import')]);
      return;
    }

    setIsImporting(true);
    setErrors([]);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        let fileData = e.target.result;
        
        // If it's an Excel file, we need to convert it to CSV format
        if (selectedFile.name.toLowerCase().endsWith('.xlsx') || selectedFile.name.toLowerCase().endsWith('.xls')) {
          try {
            // For Excel files, we'll use a simple approach to extract CSV data
            // In a production environment, you might want to use a library like SheetJS
            fileData = await convertExcelToCSV(fileData);
          } catch (error) {
            setErrors([t('failed_to_process_excel_file')]);
            setIsImporting(false);
            return;
          }
        }
        
        try {
          // Filter out empty rows from CSV data before sending to backend
          let filteredCsvData = fileData;
          
          // For both CSV and Excel files, filter out empty rows
          const lines = fileData.split('\n');
          const filteredLines = lines.filter(line => {
            const trimmedLine = line.trim();
            if (!trimmedLine) return false;
            
            // Check if line contains only commas, quotes, spaces, or empty cells
            if (/^\s*(?:[,\"]\s*)*$/.test(trimmedLine)) {
              return false;
            }
            
            // Check if line has meaningful data AND required fields
            const cells = trimmedLine.split(',');
            
            // Get the required fields (First Name, Last Name, PIN Code, Currency, Payroll Type, Salary Amount)
            const firstName = (cells[0] || '').trim().replace(/"/g, '');
            const lastName = (cells[1] || '').trim().replace(/"/g, '');
            const pinCode = (cells[2] || '').trim().replace(/"/g, '');
            const currency = (cells[6] || '').trim().replace(/"/g, '');
            const payrollType = (cells[7] || '').trim().replace(/"/g, '');
            const salaryAmount = (cells[8] || '').trim().replace(/"/g, '');
            
            // Only keep rows that have ALL required fields filled
            return firstName !== '' && firstName !== 'null' && firstName !== 'undefined' &&
                   lastName !== '' && lastName !== 'null' && lastName !== 'undefined' &&
                   pinCode !== '' && pinCode !== 'null' && pinCode !== 'undefined' &&
                   currency !== '' && currency !== 'null' && currency !== 'undefined' &&
                   payrollType !== '' && payrollType !== 'null' && payrollType !== 'undefined' &&
                   salaryAmount !== '' && salaryAmount !== 'null' && salaryAmount !== 'undefined' &&
                   !isNaN(parseFloat(salaryAmount)) && parseFloat(salaryAmount) >= 0;
          });
          
          filteredCsvData = filteredLines.join('\n');
          
          const response = await axiosClient.post('/bulk-import-staff', {
            csv_data: filteredCsvData,
            has_headers: hasHeaders
          });

          if (response.data.success) {
            setImportResult(response.data);
            setShowResultModal(true);
            setSelectedFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          } else {
            setErrors([response.data.message || 'Import failed']);
          }
        } catch (error) {
          setErrors([
            error.response?.data?.message || 
            t('failed_to_import_staff')
          ]);
        }
        setIsImporting(false);
      };

      // Read as text for CSV, as array buffer for Excel
      if (selectedFile.name.toLowerCase().endsWith('.xlsx') || selectedFile.name.toLowerCase().endsWith('.xls')) {
        reader.readAsArrayBuffer(selectedFile);
      } else {
        reader.readAsText(selectedFile);
      }
    } catch (error) {
      setErrors([t('failed_to_read_file')]);
      setIsImporting(false);
    }
  };

// Excel to CSV converter using XLSX library
const convertExcelToCSV = async (arrayBuffer) => {
  try {
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    
    // Convert worksheet to JSON to filter out empty rows
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    const originalRowCount = jsonData.length;
    
    // Filter out completely empty rows and rows with only whitespace/empty cells
    const filteredData = jsonData.filter(row => {
      if (!row || row.length === 0) return false;
      
      // Check if this row has ALL required fields filled
      const firstName = String(row[0] || '').trim();
      const lastName = String(row[1] || '').trim();
      const pinCode = String(row[2] || '').trim();
      const currency = String(row[6] || '').trim();
      const payrollType = String(row[7] || '').trim();
      const salaryAmount = String(row[8] || '').trim();
      
      // Only keep rows that have ALL required fields filled and valid
      return firstName !== '' && firstName !== 'null' && firstName !== 'undefined' &&
             lastName !== '' && lastName !== 'null' && lastName !== 'undefined' &&
             pinCode !== '' && pinCode !== 'null' && pinCode !== 'undefined' &&
             currency !== '' && currency !== 'null' && currency !== 'undefined' &&
             payrollType !== '' && payrollType !== 'null' && payrollType !== 'undefined' &&
             salaryAmount !== '' && salaryAmount !== 'null' && salaryAmount !== 'undefined' &&
             !isNaN(parseFloat(salaryAmount)) && parseFloat(salaryAmount) >= 0;
    });
    
    const filteredRowCount = filteredData.length;
    
    // Convert filtered data back to CSV
    const csvData = filteredData.map(row => 
      row.map(cell => {
        if (cell === null || cell === undefined) return '';
        const cellStr = String(cell);
        // Escape quotes and wrap in quotes if contains comma or quote
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return '"' + cellStr.replace(/"/g, '""') + '"';
        }
        return cellStr;
      }).join(',')
    ).join('\n');
    
    return csvData;
  } catch (error) {
    throw new Error('Failed to convert Excel file to CSV format.');
  }
};


  const downloadTemplate = () => {
    if (selectedTemplate === 'excel') {
      // Create Excel file
      const ws = XLSX.utils.aoa_to_sheet(excelTemplateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Staff Import Template');
      
      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'staff_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } else {
      // Create CSV file
      const blob = new Blob([csvTemplate], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'staff_import_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  };

  const getStatusBadge = (summary) => {
    if (!summary) return null;
    
    if (summary.errors > 0 && summary.successful_imports === 0) {
      return <Badge tone="critical">{t('failed')}</Badge>;
    } else if (summary.errors > 0) {
      return <Badge tone="warning">{t('partial_success')}</Badge>;
    } else {
      return <Badge tone="success">{t('success')}</Badge>;
    }
  };

  const renderImportErrors = () => {
    if (!importResult?.import_errors) return null;

    return (
      <Card>
          <TextContainer>
            <Text variant="headingMd" as="h3">
              {t('import_errors')} ({importResult.import_errors.length})
            </Text>
            <List type="bullet">
              {importResult.import_errors.map((error, index) => (
                <List.Item key={index}>
                  <Text tone="critical">{error}</Text>
                </List.Item>
              ))}
            </List>
          </TextContainer>
      </Card>
    );
  };

  return (
    <Page
      title={t('bulk_import_staff')}
      backAction={{
        content: t('staff_management'),
        onAction: () => navigate('/StaffManagement')
      }}
    >
      <Layout>
        <Layout.Section>
          <Card>
              <TextContainer>
                                 <Text variant="headingMd" as="h2">
                   {t('import_staff_from_csv_excel')}
                 </Text>
                                 <Text as="p" tone="subdued">
                   {t('import_multiple_staff_members_at_once')}
                 </Text>
              </TextContainer>

                             <BlockStack gap="500">
                 <InlineStack align="space-between" blockAlign="center">
                   <Text variant="headingMd" as="h3">
                     {t('step_1_choose_template_format')}
                   </Text>
                 </InlineStack>

                 <Card>
                   <BlockStack gap="300">
                     <Text variant="headingMd" as="h4">
                       {t('select_template_type')}
                     </Text>
                     <InlineStack gap="300" align="start">
                       <Button
                         variant={selectedTemplate === 'csv' ? 'primary' : 'secondary'}
                         onClick={() => setSelectedTemplate('csv')}
                         size="slim"
                         icon={<CSVIcon />}
                       >
                         {t('csv_template')}
                       </Button>
                       <Button
                         variant={selectedTemplate === 'excel' ? 'primary' : 'secondary'}
                         onClick={() => setSelectedTemplate('excel')}
                         size="slim"
                         icon={<ExcelIcon />}
                       >
                         {t('excel_template')}
                       </Button>
                     </InlineStack>
                     
                     <InlineStack align="space-between" blockAlign="center">
                                               <Text variant="bodyMd" as="p" tone="subdued">
                          {selectedTemplate === 'csv' 
                            ? t('simple_csv_format_with_sample')
                            : t('extended_csv_format_with_sample')
                          }
                        </Text>
                       <Button
                         icon={<DownloadIcon />}
                         onClick={downloadTemplate}
                         variant="secondary"
                       >
                         {t('download_template', { format: selectedTemplate.toUpperCase() })}
                       </Button>
                     </InlineStack>
                   </BlockStack>
                 </Card>

                <Card>
                                         <TextContainer>
                       <Text variant="headingMd" as="h4">
                         {t('file_format_requirements')}
                       </Text>
                                               <List>
                          <List.Item>
                            <Text as="span"><strong>{t('supported_formats')}:</strong> {t('csv_excel_formats')}</Text>
                          </List.Item>
                                                                               <List.Item>
                            <Text as="span"><strong>{t('required_columns')}:</strong> {t('required_columns_description')}</Text>
                          </List.Item>
                          <List.Item>
                            <Text as="span"><strong>{t('optional_columns')}:</strong> {t('optional_columns_description')}</Text>
                          </List.Item>
                          <List.Item>
                            <Text as="span"><strong>{t('important_note')}:</strong> {t('rows_missing_required_fields')}</Text>
                          </List.Item>
                           <List.Item>
                             <Text as="span"><strong>{t('pin_code_requirements')}:</strong> {t('pin_code_must_be_unique')}</Text>
                           </List.Item>
                           <List.Item>
                             <Text as="span"><strong>{t('permission_requirements')}:</strong> {t('permission_options')}</Text>
                           </List.Item>
                           <List.Item>
                             <Text as="span"><strong>{t('payroll_type_requirements')}:</strong> {t('payroll_type_options')}</Text>
                           </List.Item>
                           <List.Item>
                             <Text as="span"><strong>{t('currency_requirements')}:</strong> {t('currency_options')}</Text>
                           </List.Item>
                           <List.Item>
                             <Text as="span"><strong>{t('salary_amount_requirements')}:</strong> {t('salary_amount_must_be_valid')}</Text>
                           </List.Item>
                           <List.Item>
                             <Text as="span"><strong>{t('staff_type_auto_set')}:</strong> {t('staff_type_automatically_set')}</Text>
                           </List.Item>
                           <List.Item>
                             <Text as="span"><strong>{t('payment_method_auto_set')}:</strong> {t('payment_method_automatically_set')}</Text>
                           </List.Item>
                           <List.Item>
                             <Text as="span"><strong>{t('locations_auto_set')}:</strong> {t('locations_automatically_set')}</Text>
                           </List.Item>
                           <List.Item>
                             <Text as="span"><strong>{t('excel_files_note')}:</strong> {t('excel_files_automatically_converted')}</Text>
                           </List.Item>
                           <List.Item>
                             <Text as="span"><strong>{t('empty_rows_note')}:</strong> {t('empty_rows_automatically_skipped')}</Text>
                           </List.Item>
                        </List>
                     </TextContainer>
                </Card>

                                 <InlineStack align="space-between" blockAlign="center">
                   <Text variant="headingMd" as="h3">
                     {t('step_2_upload_file')}
                   </Text>
                 </InlineStack>

                <Card>
                    <BlockStack gap="300">
                                             <input
                         ref={fileInputRef}
                         type="file"
                         accept=".csv,.xlsx,.xls"
                         onChange={handleFileSelect}
                         style={{ display: 'none' }}
                       />
                       
                       <Button
                         onClick={() => fileInputRef.current?.click()}
                         variant="secondary"
                         fullWidth
                       >
                         {selectedFile ? selectedFile.name : t('choose_csv_or_excel_file')}
                       </Button>

                                             <InlineStack align="space-between" blockAlign="center">
                         <Text as="span">{t('first_row_contains_headers')}</Text>
                         <Button
                           onClick={() => setHasHeaders(!hasHeaders)}
                           variant="tertiary"
                           size="slim"
                         >
                           {hasHeaders ? t('yes') : t('no')}
                         </Button>
                       </InlineStack>


                      {selectedFile && (
                        <Button
                          icon={ImportIcon}
                          onClick={handleImport}
                          loading={isImporting}
                          variant="primary"
                          fullWidth
                        >
                          {isImporting ? t('importing') : t('import_staff')}
                        </Button>
                      )}
                    </BlockStack>
                </Card>

                {errors.length > 0 && (
                  <Banner tone="critical">
                    <List>
                      {errors.map((error, index) => (
                        <List.Item key={index}>{error}</List.Item>
                      ))}
                    </List>
                  </Banner>
                )}
              </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={showResultModal}
        onClose={() => setShowResultModal(false)}
        title={t('import_results')}
        primaryAction={{
          content: t('done'),
          onAction: () => {
            setShowResultModal(false);
            navigate('/StaffManagement');
          }
        }}
        secondaryActions={[
          {
            content: t('import_another_file'),
            onAction: () => {
              setShowResultModal(false);
              setImportResult(null);
            }
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="500">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingMd" as="h3">
                {t('import_summary')}
              </Text>
              {getStatusBadge(importResult?.summary)}
            </InlineStack>

            {importResult?.summary && (
              <DataTable
                columnContentTypes={['text', 'numeric', 'text']}
                headings={['Metric', 'Count', 'Status']}
                rows={[
                  [t('total_processed'), importResult.summary.total_processed, ''],
                  [t('successfully_imported'), importResult.summary.successful_imports, t('success')],
                  [t('skipped'), importResult.summary.skipped, 'Warning'],
                  [t('errors'), importResult.summary.errors, 'Critical']
                ]}
              />
            )}

                         {importResult?.message && (
               <Banner tone={importResult.summary?.errors > 0 ? 'warning' : 'success'}>
                 <p>{importResult.message}</p>
               </Banner>
             )}

            {importResult?.import_errors && importResult.import_errors.length > 0 && (
              <Card>
                  <TextContainer>
                    <Text variant="headingMd" as="h4">
                      {t('import_errors')} ({importResult.import_errors.length})
                    </Text>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <List type="bullet">
                        {importResult.import_errors.slice(0, 10).map((error, index) => (
                          <List.Item key={index}>
                            <Text tone="critical" variant="bodySm">{error}</Text>
                          </List.Item>
                        ))}
                      </List>
                      {importResult.import_errors.length > 10 && (
                        <Text tone="subdued" variant="bodySm">
                          {t('and_more_errors', { count: importResult.import_errors.length - 10 })}
                        </Text>
                      )}
                    </div>
                  </TextContainer>
              </Card>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
